import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { romAPI } from '../services/api';
import { hentVeiledning } from '../utils/ns3600-veiledning';
import { hentForskriftKontroller } from '../utils/forskrift-kontroller';
import { paragraf222Info, validerTGBeskrivelse } from '../utils/paragraf-2-22';
import { hentParagrafInfo } from '../utils/tek17-paragrafer';
import BygningsdelVurdering from '../components/BygningsdelVurdering';
import './Befaring.css';

function Befaring() {
  const { romId } = useParams();
  const navigate = useNavigate();
  const [rom, setRom] = useState(null);
  const [sporsmal, setSporsmal] = useState([]);
  const [svar, setSvar] = useState({});
  const [kommentarer, setKommentarer] = useState({}); // Kommentarer til ja/nei-svar
  const [ikkeBesvartBegrunnelse, setIkkeBesvartBegrunnelse] = useState({}); // Begrunnelse for ubesvarte obligatoriske spørsmål
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [visVeiledning, setVisVeiledning] = useState(false);
  const [forskriftSjekk, setForskriftSjekk] = useState({});
  const [bygningsdeler, setBygningsdeler] = useState([
    {
      id: 'gulv',
      navn: 'Gulv',
      tg: '',
      aarsak: '',
      konsekvens: '',
      beskrivelse: '',
      kostnadsestimat: '',
      tgiu_begrunnelse: '',
    },
    {
      id: 'vegg',
      navn: 'Vegger',
      tg: '',
      aarsak: '',
      konsekvens: '',
      beskrivelse: '',
      kostnadsestimat: '',
      tgiu_begrunnelse: '',
    },
    {
      id: 'tak',
      navn: 'Tak/himling',
      tg: '',
      aarsak: '',
      konsekvens: '',
      beskrivelse: '',
      kostnadsestimat: '',
      tgiu_begrunnelse: '',
    },
  ]);
  const [vis222Hjelp, setVis222Hjelp] = useState(false);
  const [visParagrafModal, setVisParagrafModal] = useState(false);
  const [valgtParagraf, setValgtParagraf] = useState(null);

  useEffect(() => {
    fetchData();
  }, [romId]);

  const fetchData = async () => {
    try {
      const sporsmalRes = await romAPI.getSporsmal(romId);

      setRom(sporsmalRes.data.rom);
      setSporsmal(sporsmalRes.data.sporsmal);

      // Initialiser svar og kommentarer fra eksisterende data
      const initialSvar = {};
      const initialKommentarer = {};

      sporsmalRes.data.sporsmal.forEach((s) => {
        if (s.svar_verdi) {
          // Sjekk om svaret inneholder en kommentar (format: "ja | Kommentar: ...")
          if (s.svar_verdi.includes(' | Kommentar: ')) {
            const [baseSvar, kommentarDel] = s.svar_verdi.split(' | Kommentar: ');
            initialSvar[s.id] = baseSvar;
            initialKommentarer[s.id] = kommentarDel;
          } else {
            initialSvar[s.id] = s.svar_verdi;
          }
        }
      });

      setSvar(initialSvar);
      setKommentarer(initialKommentarer);
    } catch (err) {
      setError('Kunne ikke hente befaring data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSvarChange = (sporsmalId, verdi) => {
    setSvar({
      ...svar,
      [sporsmalId]: verdi,
    });
  };

  const handleBygningsdelChange = (delId, field, value) => {
    setBygningsdeler(bygningsdeler.map(del =>
      del.id === delId ? { ...del, [field]: value } : del
    ));
  };

  const visParagrafDetaljer = (paragrafRef) => {
    const info = hentParagrafInfo(paragrafRef);
    if (info) {
      setValgtParagraf(info);
      setVisParagrafModal(true);
    }
  };

  const handleSave = async () => {
    const valideringsFeil = [];

    // Valider obligatoriske spørsmål
    const obligatoriskeSporsmal = sporsmal.filter(s => s.obligatorisk);
    const manglerSvar = [];

    for (const s of obligatoriskeSporsmal) {
      const harSvar = svar[s.id] && svar[s.id].trim() !== '';
      const harBegrunnelse = ikkeBesvartBegrunnelse[s.id] && ikkeBesvartBegrunnelse[s.id].trim().length >= 10;

      if (!harSvar && !harBegrunnelse) {
        manglerSvar.push(`• ${s.sporsmal_tekst} - Må enten besvares eller ha begrunnelse for hvorfor det ikke besvares`);
      }
    }

    if (manglerSvar.length > 0) {
      valideringsFeil.push({
        kategori: 'Obligatoriske spørsmål (merket med *)',
        feil: manglerSvar,
      });
    }

    // Valider obligatoriske forskriftskontroller
    const forskriftKontroller = hentForskriftKontroller(rom.romtype);

    const manglerObligatoriske = forskriftKontroller
      .filter(k => k.obligatorisk)
      .filter(k => !forskriftSjekk[k.id]);

    if (manglerObligatoriske.length > 0) {
      valideringsFeil.push({
        kategori: 'Obligatoriske kontroller',
        feil: manglerObligatoriske.map(k => `• ${k.tittel}`),
      });
    }

    // Valider TG2/TG3 har årsak og konsekvens (§2-22)
    const bygningsdelFeil = [];
    for (const del of bygningsdeler) {
      if (del.tg) {
        // Separat validering for TGIU
        if (del.tg === 'TGIU') {
          if (!del.tgiu_begrunnelse || del.tgiu_begrunnelse.trim().length < 10) {
            bygningsdelFeil.push(`• ${del.navn}: TGIU krever begrunnelse - hvorfor er bygningsdelen ikke undersøkt? (minimum 10 tegn)`);
          }
        } else {
          // Valider TG2/TG3 årsak og konsekvens
          const validering = validerTGBeskrivelse(del.tg, del.aarsak, del.konsekvens);
          if (!validering.gyldig) {
            bygningsdelFeil.push(`• ${del.navn}: ${validering.feil}`);
          }

          // Sjekk kostnadsestimat for TG3
          if (del.tg === 'TG3' && !del.kostnadsestimat) {
            bygningsdelFeil.push(`• ${del.navn}: Mangler kostnadsestimat (obligatorisk for TG3)`);
          }
        }
      }
    }

    if (bygningsdelFeil.length > 0) {
      valideringsFeil.push({
        kategori: 'Bygningsdeler (§2-22)',
        feil: bygningsdelFeil,
      });
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const svarArray = Object.entries(svar).map(([sporsmalId, verdi]) => {
        const kommentar = kommentarer[sporsmalId];
        let svarVerdi = verdi;

        // Hvis det finnes en kommentar, legg den til svaret
        if (kommentar && kommentar.trim() !== '') {
          svarVerdi = `${verdi} | Kommentar: ${kommentar}`;
        }

        return {
          sporsmal_id: parseInt(sporsmalId),
          svar_verdi: svarVerdi,
        };
      });

      // Legg til begrunnelser for ubesvarte obligatoriske spørsmål
      const begrunnelserArray = Object.entries(ikkeBesvartBegrunnelse)
        .filter(([_, begrunnelse]) => begrunnelse && begrunnelse.trim() !== '')
        .map(([sporsmalId, begrunnelse]) => ({
          sporsmal_id: parseInt(sporsmalId),
          svar_verdi: `[IKKE BESVART] Begrunnelse: ${begrunnelse}`,
        }));

      await romAPI.saveSporsmalSvar(romId, {
        svar: [...svarArray, ...begrunnelserArray]
      });
      // TODO: Lagre bygningsdeler også

      // Vis resultat basert på om det er mangler eller ikke
      if (valideringsFeil.length === 0) {
        setSuccess('✅ Svar lagret! Rombefaringen er fullført og klar for rapportgenerering.');
        setError('');
      } else {
        setSuccess('✓ Svar lagret!');

        // Vis tydelig advarsel om mangler
        let mangelmelding = '⚠️ ADVARSEL: Rombefaringen har mangler som må utbedres før rapport kan genereres.\n\n';
        valideringsFeil.forEach(v => {
          mangelmelding += `${v.kategori}:\n`;
          v.feil.forEach(f => mangelmelding += `${f}\n`);
          mangelmelding += '\n';
        });

        // Legg til hjelpetekst hvis obligatoriske kontroller mangler
        if (valideringsFeil.some(v => v.kategori.includes('Obligatoriske kontroller'))) {
          mangelmelding += '💡 Tips: Scroll ned til den gule boksen "Obligatoriske kontroller" og kryss av for alle kontrollene.\n\n';
        }

        mangelmelding += '→ Se kontrollsenteret for fullstendig oversikt over alle mangler i rapporten.';
        setError(mangelmelding);
      }

      // Scroll til toppen for å se meldingene
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Kunne ikke lagre svar');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const renderSporsmalInput = (s) => {
    const value = svar[s.id] || '';

    switch (s.svar_type) {
      case 'ja_nei':
        return (
          <div>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name={`sporsmal_${s.id}`}
                  value="ja"
                  checked={value === 'ja'}
                  onChange={(e) => handleSvarChange(s.id, e.target.value)}
                />
                Ja
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name={`sporsmal_${s.id}`}
                  value="nei"
                  checked={value === 'nei'}
                  onChange={(e) => handleSvarChange(s.id, e.target.value)}
                />
                Nei
              </label>
              {!s.obligatorisk && (
                <label className="radio-label">
                  <input
                    type="radio"
                    name={`sporsmal_${s.id}`}
                    value="ukjent"
                    checked={value === 'ukjent'}
                    onChange={(e) => handleSvarChange(s.id, e.target.value)}
                  />
                  Ukjent
                </label>
              )}
            </div>
            {/* Kommentarfelt for alle ja/nei-spørsmål */}
            <div style={{ marginTop: '10px' }}>
              <textarea
                value={kommentarer[s.id] || ''}
                onChange={(e) => setKommentarer({
                  ...kommentarer,
                  [s.id]: e.target.value
                })}
                placeholder="Kommentar/presisering (f.eks. oppfyller byggeårets forskrift, avvik fra dagens krav, etc.)"
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>
        );

      case 'dropdown':
        const alternativer = s.svar_alternativer
          ? JSON.parse(s.svar_alternativer)
          : [];
        return (
          <select
            value={value}
            onChange={(e) => handleSvarChange(s.id, e.target.value)}
            required={s.obligatorisk}
            className="sporsmal-select"
          >
            <option value="">Velg...</option>
            {alternativer.map((alt, idx) => (
              <option key={idx} value={alt}>
                {alt}
              </option>
            ))}
          </select>
        );

      case 'tall':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleSvarChange(s.id, e.target.value)}
            required={s.obligatorisk}
            className="sporsmal-input"
            placeholder="Skriv inn tall..."
          />
        );

      case 'tekst':
        return (
          <textarea
            value={value}
            onChange={(e) => handleSvarChange(s.id, e.target.value)}
            required={s.obligatorisk}
            className="sporsmal-textarea"
            rows="3"
            placeholder="Skriv inn svar..."
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleSvarChange(s.id, e.target.value)}
            required={s.obligatorisk}
            className="sporsmal-input"
          />
        );
    }
  };

  const grupperteSporsmal = sporsmal.reduce((acc, s) => {
    const kategori = s.sporsmal_kategori || 'generelt';
    if (!acc[kategori]) {
      acc[kategori] = [];
    }
    acc[kategori].push(s);
    return acc;
  }, {});

  const kategoriNavn = (kat) => {
    const navn = {
      overflater: 'Overflater',
      overflater_gulv: 'Gulv',
      membran: 'Membran og sluk',
      hulltaking: 'Hulltaking',
      ventilasjon: 'Ventilasjon',
      innredning: 'Innredning',
      sikkerhet: 'Sikkerhet',
      generelt: 'Generelt',
    };
    return navn[kat] || kat;
  };

  const romtypeNavn = (type) => {
    const typer = {
      bad: 'Bad',
      wc: 'WC',
      vaskerom: 'Vaskerom',
      kjokken: 'Kjøkken',
      stue: 'Stue',
      soverom: 'Soverom',
      gang: 'Gang',
      bod: 'Bod',
      annet: 'Annet',
    };
    return typer[type] || type;
  };

  if (loading) return <div className="loading">Laster befaring...</div>;
  if (error && !rom) return <div className="error-message">{error}</div>;
  if (!rom) return <div className="error-message">Rom ikke funnet</div>;

  const veiledning = hentVeiledning(rom.romtype);
  const forskriftKontroller = hentForskriftKontroller(rom.romtype);
  const alleObligatoriskeSjekket = forskriftKontroller
    .filter(k => k.obligatorisk)
    .every(k => forskriftSjekk[k.id]);

  return (
    <div className="befaring-page">
      <nav className="navbar">
        <div className="navbar-content">
          <button onClick={() => navigate(`/eiendom/${rom.eiendom_id}`)} className="btn-back">
            ← Tilbake til bygningsoversikt
          </button>
          <h1>Befaring</h1>
        </div>
      </nav>

      <div className="befaring-content">
        <div className="befaring-header">
          <div>
            <h2>
              {romtypeNavn(rom.romtype)} {rom.romnummer}
            </h2>
            <p className="rom-info">
              Tilstandsgrad: <strong>{rom.tilstandsgrad || 'Ikke satt'}</strong>
            </p>
            <p className="rom-info">
              Etasje:{' '}
              {rom.etasje?.replace('etasje_', '').replace('_', '. etasje') || 'Ukjent'}
            </p>
          </div>
          <div className="save-section">
            {veiledning && (
              <button
                onClick={() => setVisVeiledning(!visVeiledning)}
                className="btn-help"
              >
                📖 NS3600 Veiledning
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary btn-save"
            >
              {saving ? 'Lagrer...' : 'Lagre svar'}
            </button>
          </div>
        </div>

        {visVeiledning && veiledning && (
          <div className="veiledning-modal">
            <div className="veiledning-content">
              <div className="veiledning-header">
                <h3>{veiledning.tittel}</h3>
                <button
                  onClick={() => setVisVeiledning(false)}
                  className="btn-close"
                >
                  ✕
                </button>
              </div>

              <div className="veiledning-body">
                {veiledning.undersokelser && (
                  <div className="veiledning-section">
                    <h4>Undersøkelser som skal utføres:</h4>
                    {veiledning.undersokelser.map((u, idx) => (
                      <div key={idx} className="undersokelse-kategori">
                        <h5>{u.kategori}</h5>
                        <ul>
                          {u.punkter.map((p, i) => (
                            <li key={i}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {veiledning.tilstandsgrader && (
                  <div className="veiledning-section">
                    <h4>Tilstandsgrader:</h4>
                    {Object.entries(veiledning.tilstandsgrader).map(([tg, info]) => (
                      <div key={tg} className="tg-veiledning">
                        <h5 className={`tg-badge tg-${tg.toLowerCase()}`}>
                          {tg}: {typeof info === 'object' ? info.beskrivelse : info}
                        </h5>
                        {typeof info === 'object' && info.kriterier && (
                          <ul>
                            {info.kriterier.map((k, i) => (
                              <li key={i}>{k}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {veiledning.referanser && (
                  <div className="veiledning-section">
                    <h4>Referanser:</h4>
                    <ul className="referanser">
                      {veiledning.referanser.map((ref, idx) => (
                        <li key={idx}>{ref}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {success && <div className="success-message">{success}</div>}
        {error && <div className="error-message">{error}</div>}

        {forskriftKontroller.length > 0 && (
          <div className="forskrift-sjekkliste">
            <div className="forskrift-header">
              <h3>⚠️ Obligatoriske kontroller (Forskrift §2-18)</h3>
              {!alleObligatoriskeSjekket && (
                <p className="forskrift-advarsel">
                  Du må bekrefte alle obligatoriske kontroller før lagring
                </p>
              )}
            </div>

            <div className="forskrift-liste">
              {forskriftKontroller.map((kontroll) => {
                const erKryssetAv = forskriftSjekk[kontroll.id] || false;

                return (
                  <div
                    key={kontroll.id}
                    className={`forskrift-item ${kontroll.obligatorisk ? 'obligatorisk' : ''}`}
                    style={{
                      backgroundColor: erKryssetAv ? '#e8f5e9' : '#fff3cd',
                      borderLeft: `4px solid ${erKryssetAv ? '#4caf50' : '#ffc107'}`
                    }}
                  >
                    <label className="forskrift-checkbox">
                      <input
                        type="checkbox"
                        checked={erKryssetAv}
                        onChange={(e) => {
                          setForskriftSjekk({
                            ...forskriftSjekk,
                            [kontroll.id]: e.target.checked,
                          });
                        }}
                      />
                      <div className="forskrift-innhold">
                        <div className="forskrift-tittel">
                          {kontroll.tittel}
                          {kontroll.obligatorisk && <span className="required">*</span>}
                          <button
                            type="button"
                            className="forskrift-paragraf"
                            onClick={() => visParagrafDetaljer(kontroll.paragraf)}
                            title={`Klikk for å se detaljer om ${kontroll.paragraf}`}
                          >
                            {kontroll.paragraf} ℹ️
                          </button>
                        </div>
                        <div className="forskrift-beskrivelse">
                          {kontroll.beskrivelse}
                        </div>
                        {kontroll.konsekvens && !erKryssetAv && (
                          <div className="forskrift-konsekvens">
                            ⚠️ {kontroll.konsekvens}
                          </div>
                        )}
                        {erKryssetAv && (
                          <div style={{
                            marginTop: '8px',
                            padding: '6px 10px',
                            backgroundColor: '#c8e6c9',
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#2e7d32',
                            fontWeight: '500'
                          }}>
                            ✅ Kontroll utført og godkjent
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <BygningsdelVurdering
          bygningsdeler={bygningsdeler}
          onChange={handleBygningsdelChange}
          vis222Hjelp={vis222Hjelp}
          setVis222Hjelp={setVis222Hjelp}
          paragraf222Info={paragraf222Info}
        />

        {sporsmal.length === 0 ? (
          <div className="empty-state">
            <p>Ingen spørsmål definert for denne romtypen ennå</p>
          </div>
        ) : (
          <div className="sporsmal-sections">
            {Object.entries(grupperteSporsmal).map(([kategori, sporsmalListe]) => (
              <div key={kategori} className="sporsmal-kategori">
                <h3 className="kategori-overskrift">{kategoriNavn(kategori)}</h3>

                <div className="sporsmal-liste">
                  {sporsmalListe
                    .sort((a, b) => a.sortering - b.sortering)
                    .map((s) => (
                      <div key={s.id} className="sporsmal-card">
                        <div className="sporsmal-header">
                          <label className="sporsmal-label">
                            {s.sporsmal_tekst}
                            {s.obligatorisk ? <span className="required">*</span> : ''}
                          </label>
                          {s.ns3600_referanse && (
                            <span className="ns3600-ref">{s.ns3600_referanse}</span>
                          )}
                        </div>
                        {renderSporsmalInput(s)}

                        {/* Vis begrunnelsesfelt for obligatoriske spørsmål uten svar */}
                        {s.obligatorisk && (!svar[s.id] || svar[s.id].trim() === '') && (
                          <div className="ikke-besvart-container" style={{marginTop: '10px'}}>
                            {/* Vis gul varselboks kun før brukeren begynner å skrive */}
                            {(!ikkeBesvartBegrunnelse[s.id] || ikkeBesvartBegrunnelse[s.id].trim() === '') ? (
                              <div style={{padding: '10px', backgroundColor: '#fff3cd', borderLeft: '3px solid #ffc107', marginBottom: '10px'}}>
                                <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold'}}>
                                  ⚠️ Dette spørsmålet er obligatorisk
                                </p>
                                <p style={{margin: '0', fontSize: '13px'}}>
                                  Enten besvar spørsmålet ovenfor, eller forklar hvorfor det ikke kan besvares nedenfor.
                                </p>
                              </div>
                            ) : (
                              <div style={{marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px'}}>
                                <span style={{fontSize: '12px', color: '#666', fontStyle: 'italic'}}>
                                  Obligatorisk begrunnelse (må ikke besvares)
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {/* Info-dialog kan implementeres senere */}}
                                  style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#666'}}
                                  title="Klikk for mer informasjon om obligatoriske spørsmål"
                                >
                                  ℹ️
                                </button>
                              </div>
                            )}

                            <textarea
                              value={ikkeBesvartBegrunnelse[s.id] || ''}
                              onChange={(e) => setIkkeBesvartBegrunnelse({
                                ...ikkeBesvartBegrunnelse,
                                [s.id]: e.target.value
                              })}
                              placeholder="F.eks: Ikke tilgjengelig for inspeksjon, skjult av fast innredning, etc. (minimum 10 tegn)"
                              style={{
                                width: '100%',
                                minHeight: '60px',
                                padding: '8px',
                                fontSize: '13px',
                                border: (!ikkeBesvartBegrunnelse[s.id] || ikkeBesvartBegrunnelse[s.id].trim() === '')
                                  ? '2px solid #ffc107'  // Gul kant før skrivstart
                                  : '1px solid #ddd',     // Normal kant når de skriver
                                borderRadius: '4px'
                              }}
                            />
                            {ikkeBesvartBegrunnelse[s.id] && ikkeBesvartBegrunnelse[s.id].length < 10 && (
                              <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#856404'}}>
                                Begrunnelsen må være minst 10 tegn ({ikkeBesvartBegrunnelse[s.id].length}/10)
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="befaring-footer">
          <button onClick={() => navigate(`/eiendom/${rom.eiendom_id}`)} className="btn-secondary">
            Tilbake til bygningsoversikt
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Lagrer...' : 'Lagre'}
          </button>
        </div>
      </div>

      {visParagrafModal && valgtParagraf && (
        <div className="veiledning-modal">
          <div className="veiledning-content">
            <div className="veiledning-header">
              <h3>{valgtParagraf.tittel}</h3>
              <button
                onClick={() => setVisParagrafModal(false)}
                className="btn-close"
              >
                ✕
              </button>
            </div>

            <div className="veiledning-body">
              <div className="veiledning-section">
                <h4>Sammendrag:</h4>
                <p style={{ whiteSpace: 'pre-line' }}>{valgtParagraf.sammendrag}</p>
              </div>

              {valgtParagraf.konsekvens && (
                <div className="veiledning-section">
                  <h4>Konsekvens ved avvik:</h4>
                  <div className="paragraf-konsekvens-box">
                    ⚠️ {valgtParagraf.konsekvens}
                  </div>
                </div>
              )}

              {valgtParagraf.tilstandsgrad && (
                <div className="veiledning-section">
                  <h4>Tilstandsgrad:</h4>
                  <p><strong>{valgtParagraf.tilstandsgrad}</strong></p>
                </div>
              )}

              <div className="veiledning-section">
                <h4>Referanse:</h4>
                <p className="paragraf-referanse">
                  {valgtParagraf.referanse}
                  {valgtParagraf.lovdataUrl && (
                    <>
                      <br />
                      <a
                        href={valgtParagraf.lovdataUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lovdata-link"
                      >
                        📖 Les fullstendig lovtekst på Lovdata
                      </a>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Befaring;
